import NiceModal from '@ebay/nice-modal-react';
import VoiceAgentModal from '@components/features/modals/elevenlabs/VoiceAgentModal';
import { store } from '@store/index';
import { getUserHeaderInfo } from '@store/reducers/userSelector';

/** SessionStorage key for pending voice agent modal after login */
const VOICE_AGENT_PENDING_KEY = 'bj:voice-agent-pending';

/** Query param flag added to URL for auto-open after login redirect */
export const VOICE_AGENT_QUERY_PARAM = 'openVoiceAgent';

/**
 * Check if the user is authenticated by reading the Redux store.
 * The headerInfo is populated when the user is logged in (via useHeaderInfoData in the Header).
 * This works outside React components (no hooks needed).
 */
const isAuthenticated = (): boolean => {
  try {
    const state = store.getState();
    return !!getUserHeaderInfo(state);
  } catch {
    return false;
  }
};

/**
 * @description Show VoiceAgentModal with authentication check.
 * - If authenticated → shows the modal immediately
 * - If not authenticated → saves modal params to sessionStorage and redirects to login.
 *   After login, the user is redirected back to the original page with a query param flag.
 *   The `useVoiceAgentModalAutoOpen` hook picks up the flag and opens the modal.
 *
 * @param agentId - The ElevenLabs agent ID
 * @param locale - The current app locale
 */
export const showVoiceAgentModalWithAuth = (agentId: string, locale: Locale): void => {
  if (isAuthenticated()) {
    // User is authenticated — show modal directly
    void NiceModal.show(VoiceAgentModal, { agentId, locale });
  } else {
    // User is NOT authenticated — save params and redirect to login
    try {
      sessionStorage.setItem(VOICE_AGENT_PENDING_KEY, JSON.stringify({ agentId, locale }));
    } catch {
      // sessionStorage not available — ignore silently
    }

    // Build the target path: current page URL + openVoiceAgent flag
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set(VOICE_AGENT_QUERY_PARAM, '1');
    const targetPath = `${currentUrl.pathname}${currentUrl.search}`;

    // Redirect to login with targetPath for redirect-back after auth
    window.location.href = `/login?targetPath=${encodeURIComponent(targetPath)}`;
  }
};

/**
 * @description Get and clear the pending voice agent modal params from sessionStorage.
 * Used by `useVoiceAgentModalAutoOpen` hook after login redirect.
 * @returns The saved params or null if none exist
 */
export const consumePendingVoiceAgent = (): { agentId: string; locale: Locale } | null => {
  try {
    const raw = sessionStorage.getItem(VOICE_AGENT_PENDING_KEY);
    if (!raw) return null;

    sessionStorage.removeItem(VOICE_AGENT_PENDING_KEY);
    return JSON.parse(raw) as { agentId: string; locale: Locale };
  } catch {
    return null;
  }
};
